/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { identity, isEmpty } from 'lodash'
import { useSearchParams } from 'react-router-dom'

export function getQuerystringItems(qs?: string): Record<string, string | null> {
  return Object.fromEntries(new URLSearchParams(qs ?? window.location.search))
}

export function getHashItems(hash?: string): Record<string, string | null> {
  return Object.fromEntries(
    new URLSearchParams(hash ? decodeURIComponent(hash) : window.location.hash),
  )
}

export function getQuerystringItem<TSchema extends Record<string, string | boolean | null>>(
  key: Extract<keyof TSchema, string>,
): string | null {
  return new URLSearchParams(window.location.search).get(key)
}

/**
 * A custom React hook that manages query string parameters in the URL.
 *
 * @template TSchema - The schema type for parsed query parameters, must be a record with string, boolean, or undefined values
 * @param parseFn - Optional function to transform the raw query string into the specified schema type.
 *                  Defaults to identity function if not provided.
 *
 * @returns An object containing:
 *   - queryStringItems: The current query parameters parsed according to the provided schema
 *   - setQueryStringItem: Function to update a single query parameter
 *   - setQueryStringItems: Function to update multiple query parameters at once
 *
 * @example
 * // Basic usage with default parser
 * const { queryStringItems, setQueryStringItem } = useQueryStringParams();
 *
 * @example
 * // With custom parser for specific types
 * const { queryStringItems, setQueryStringItems } = useQueryStringParams<{page: string, isActive: boolean}>(
 *   (rawParams) => ({
 *     page: String(rawParams.page || '1'),
 *     isActive: Boolean(rawParams.isActive)
 *   })
 * );
 */
export function useQueryStringParams<TSchema extends Record<string, string | boolean | undefined>>(
  parseFn: (value: unknown) => TSchema = identity,
) {
  const [searchParams, setSearchParams] = useSearchParams()

  return {
    queryStringItems: parseFn(getQuerystringItems(searchParams.toString())),
    setQueryStringItem(key: string, value: string | null | undefined): void {
      setQueryStringItems({ [key]: value })
    },
    setQueryStringItems,
  }

  /**
   * Updates the query string parameters in the URL.
   *
   * @param items - A record of key-value pairs where the key is the parameter name
   *                and the value is the parameter value. If the value is null or undefined,
   *                the parameter is removed from the query string.
   */
  function setQueryStringItems(items: Record<string, string | null | undefined>): void {
    const newParams = new URLSearchParams(searchParams)

    Object.entries(items).forEach(([key, value]) => {
      if (isEmpty(value)) {
        newParams.delete(key)
      } else if (value) {
        newParams.set(key, value.toString())
      }
    })

    setSearchParams(newParams)
  }
}
